import { useTranslation } from 'react-i18next'
import { format, parseISO, isValid } from 'date-fns'
import { CalendarDays, ChevronRight, Trash2 } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { statusBadgeVariant } from './plan-helpers'
import type { TrainingPlan } from '../../types/training-plan'

interface PlanCardProps {
  plan: TrainingPlan
  currentUserId: string
  onOpen: (plan: TrainingPlan) => void
  onDelete?: (plan: TrainingPlan) => void
  onAccept?: (plan: TrainingPlan) => void
  onReject?: (plan: TrainingPlan) => void
  busy?: boolean
}

function formatDate(value: string | null): string | null {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? format(parsed, 'PP') : value
}

export function PlanCard({
  plan,
  currentUserId,
  onOpen,
  onDelete,
  onAccept,
  onReject,
  busy = false,
}: PlanCardProps) {
  const { t } = useTranslation()

  const isCoach = plan.coach?.id === currentUserId
  // The coach reviews the athlete's pending invitation.
  const isInvitation = isCoach && plan.status === 'pending'
  const startDate = formatDate(plan.start_date)
  const title = plan.goal?.trim() || t(`trainingPlans.discipline.${plan.discipline ?? 'bouldering'}`)
  const discipline = t(`trainingPlans.discipline.${plan.discipline ?? 'bouldering'}`)

  // Counterpart line: athletes see who coaches them (coaches lead with the
  // athlete instead, shown via avatar below).
  const counterpart = plan.coach
    ? { label: t('trainingPlans.card.coach'), name: plan.coach.name || plan.coach.username }
    : { label: '', name: t('trainingPlans.card.self') }

  // For coached plans the athlete is the headline; otherwise it's the plan goal.
  const athleteInitials = (plan.user.name || plan.user.username).slice(0, 2).toUpperCase()
  const coachedSubtitle = plan.goal?.trim() ? `${discipline} · ${plan.goal.trim()}` : discipline

  return (
    <Card className="p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        {isCoach ? (
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar className="size-9 shrink-0">
              <AvatarImage src={plan.user.avatar_small_url ?? undefined} alt={plan.user.username} />
              <AvatarFallback>{athleteInitials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-medium truncate">{plan.user.username}</p>
              <p className="text-xs text-muted-foreground truncate">{coachedSubtitle}</p>
            </div>
          </div>
        ) : (
          <div className="min-w-0">
            <p className="font-medium truncate">{title}</p>
            <p className="text-xs text-muted-foreground truncate">
              {counterpart.label ? `${counterpart.label}: ${counterpart.name}` : counterpart.name}
            </p>
          </div>
        )}
        <Badge variant={statusBadgeVariant(plan.status)}>
          {t(`trainingPlans.status.${plan.status}`)}
        </Badge>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>{t('trainingPlans.card.weeks', { count: plan.length_weeks ?? 0 })}</span>
        <span className="inline-flex items-center gap-1">
          <CalendarDays className="size-3.5" />
          {startDate ? t('trainingPlans.card.startsOn', { date: startDate }) : t('trainingPlans.card.noStartDate')}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {isInvitation ? (
          <>
            <Button size="sm" disabled={busy} onClick={() => onAccept?.(plan)}>
              {t('trainingPlans.invitation.accept')}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onReject?.(plan)}>
              {t('trainingPlans.invitation.reject')}
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="secondary" onClick={() => onOpen(plan)}>
              {t('trainingPlans.actions.open')}
              <ChevronRight className="size-4" />
            </Button>
            {onDelete && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onDelete(plan)}
                className="text-muted-foreground hover:text-destructive ml-auto"
                aria-label={t('trainingPlans.actions.delete')}
              >
                <Trash2 className="size-4" />
              </Button>
            )}
          </>
        )}
      </div>
    </Card>
  )
}
